/* eslint-disable max-lines-per-function */

import Convertable from '@/classes/Convertable'
import { KeyDict } from '@/types'
import { CastType } from '@/types/casts'
import { SourceStream, StreamType } from '@/types/streams'
import firebase from 'firebase/app'
import { CastApiErrorCode } from '../types/errors'
import CastAPI, { CastParamsOptional } from './CastAPI'
import { generateRandomString } from '@/modules/common/utils'
import { Result } from '@/modules/errorHandling/results'
import { CollectionNames } from '@/modules/database/utils'
import { Cast } from './cast'
import { CastSwitcher } from './CastSwitcher'
import { SceneSourceType } from '@/modules/scenes/types'
import { Scene } from '@/types/scenes'
import { getAudioTracksForCount } from '@/modules/audiomultitrack/utils/audioTracks'
import { DEFAULT_TRACKS_SWITCHER } from '@/modules/audiomultitrack/types'

export type SwitcherCastParamsSaveCast = Pick<CastSwitcher, 'name' | 'team_id' | 'start_date' | 'end_date' | 'widgets' |
'scenes' | 'invited_casters' | 'isTempCast'> & { originalCast?: string|null }

export default class SwitcherCastAPI extends CastAPI {

  private _castDocRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>|null = null

  override async createCast (castParams: SwitcherCastParamsSaveCast, currentUserId: string,
    _optionalParams?: CastParamsOptional): Promise<Result<Cast, CastApiErrorCode>>
  {
    this._teamId = castParams.team_id
    const version = await this.getDefaultVersion()
    try {
      this._castDocRef = this._db.collection(CollectionNames.CASTS).doc()
      const result = await this._db.runTransaction(async (transaction: firebase.firestore.Transaction) => {
        this._transaction = transaction
        if (this._castDocRef === null) {
          return Result.fail(CastApiErrorCode.CREATE_CAST, 'Could not create cast document')
        }
        const castId = this._castDocRef.id
        const eventId = this.createBlankEvent({ ...castParams, castId, castType: CastType.Switcher })
        const sourceStreams = this.setupSourceStreams(castParams)
        for (const sourceStream of Object.values(sourceStreams)) {
          if (sourceStream.type !== StreamType.Playlist) continue
          sourceStream.active = this.hasPlaylistSource(castParams.scenes)
        }
        const token = generateRandomString(24)
        const cast = this.saveCast(castId, eventId, castParams, sourceStreams, token, version, currentUserId)
        this.setLastEventTimestamp()
        this.createPointerData(castId, castParams.name, token, version)
        this.createVideoPlayer(castId)
        this.createReplaySession(castId)
        return cast
      })
      if (result.isSuccess) return result
      else return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
    } catch (error) {
      console.error(`Error creating cast ${ error }`)
    }
    return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
  }

  async updateCast (castParams: Cast & { id: string, team_id: string }): Promise<Result<Cast, CastApiErrorCode>> {
    this._teamId = castParams.team_id ?? null
    try {
      const cast = await this.updateExistingCast(castParams)
      if (cast.isSuccess) {
        return Result.success(new CastSwitcher(cast.value))
      }
    } catch (error) {
      console.error('Error updating cast', error)
    }
    return Result.fail(CastApiErrorCode.UPDATE_CAST, 'Error updating cast')
  }

  private async saveCast (castId: string, eventId: string, castParams: SwitcherCastParamsSaveCast,
                          sourceStreams: KeyDict<SourceStream>, token: string, version: string,
                          currentUserId: string): Promise<Result<CastSwitcher, CastApiErrorCode>> {
    const chatDocRef = this._db.collection(CollectionNames.CHATS).doc()
    this._transaction?.set(chatDocRef, { team_id: castParams.team_id, messages: [] })
    const cast = new CastSwitcher({
      ...castParams, id: castId, event_id: eventId,
      castType: CastType.Switcher,
      token, online_sessions: {},
      chat_id: chatDocRef.id,
      widgets: {},
      source_streams: sourceStreams,
      version: version,
      creator_id: currentUserId,
      anonymous_caster_rules: this.getAnonymousCasterRules(),
      record_isos: true,
      audioTracks: getAudioTracksForCount(DEFAULT_TRACKS_SWITCHER)
    })
    if (this._castDocRef === null) {
      return Result.fail(CastApiErrorCode.CREATE_CAST, 'Could not create cast document')
    }
    this._transaction?.set(this._castDocRef, Convertable.toObject({ ...cast }))
    const widgets = Convertable.toObject(await this.setupWidgets({ ...castParams, id: cast.id }))
    this._transaction?.set(this._castDocRef, { id: cast.id, widgets }, { merge: true })
    return Result.success(cast)
  }

  protected override getAnonymousCasterRules () {
    return {
      max_anonymous_casters: 0,
      max_total_casters: 0,
      max_anonymous_accounts: 50
    }
  }

  private hasPlaylistSource (scenes: KeyDict<Scene>) {
    return Object.values(scenes).some((scene) => scene.sourceType === SceneSourceType.Playlist)
  }
}
